import { useQuery } from '@tanstack/react-query';

import { isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { monitoringKeys } from '@/shared/lib/query/queryKeys';

import {
  BYTES_TO_GB,
  getInstantValue,
  getRangeValues,
  getRangeValuesByPod,
  getValuesByPod,
  PROMETHEUS_QUERIES,
  QUERY_INTERVALS,
  RANGE_SECONDS,
  REFETCH_INTERVAL,
  STALE_TIME,
} from '@/features/monitoring/api';
import type { ResourceUsageData } from '@/features/monitoring/api';

import { assignOrderedPodSeriesColors } from './lib/assignOrderedPodSeriesColors';

export const useMemoryUsage = () => {
  const enabled = isPrometheusConfigured();

  const summaryQuery = useQueryWithBoundary<Pick<ResourceUsageData, 'current' | 'total' | 'percentage' | 'unit'>>({
    queryKey: monitoringKeys.memory(),
    queryFn: async () => {
      const [currentBytes, requestBytes] = await Promise.all([
        getInstantValue(PROMETHEUS_QUERIES.MEMORY.TOTAL),
        getInstantValue(PROMETHEUS_QUERIES.MEMORY.REQUEST),
      ]);

      const current = currentBytes / BYTES_TO_GB;
      const total = (requestBytes || BYTES_TO_GB) / BYTES_TO_GB;
      const percentage = (current / total) * 100;

      return {
        current,
        total,
        percentage,
        unit: 'GB',
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const detailQuery = useQuery<Pick<ResourceUsageData, 'byPod' | 'history' | 'podHistory'>>({
    queryKey: [...monitoringKeys.memoryPrefix(), 'detail'],
    queryFn: async () => {
      const now = Math.floor(Date.now() / 1000);

      const [byPod, history, podHistoryRaw] = await Promise.all([
        getValuesByPod(PROMETHEUS_QUERIES.MEMORY.BY_POD),
        getRangeValues(PROMETHEUS_QUERIES.MEMORY.HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.MEMORY.BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
      ]);

      const podHistory = assignOrderedPodSeriesColors(
        podHistoryRaw.map((pod) => ({
          pod: pod.pod,
          data: pod.data.map((point) => ({ ...point, value: point.value / BYTES_TO_GB })),
        }))
      );

      return {
        byPod: byPod.map((pod) => ({ ...pod, value: pod.value / BYTES_TO_GB })).sort((a, b) => b.value - a.value),
        podHistory,
        history: history.map((point) => ({ ...point, value: point.value / BYTES_TO_GB })),
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data: ResourceUsageData | undefined = summaryQuery.data
    ? {
        ...summaryQuery.data,
        byPod: detailQuery.data?.byPod ?? [],
        history: detailQuery.data?.history ?? [],
        podHistory: detailQuery.data?.podHistory ?? [],
      }
    : undefined;

  return {
    ...summaryQuery,
    data,
    isFetching: summaryQuery.isFetching || detailQuery.isFetching,
    isLoading: summaryQuery.isLoading,
  };
};
